import React, { memo, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Button from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import DocumentIcon from '@atlaskit/icon/glyph/document';
import SectionMessage from '@atlaskit/section-message';
import Tooltip from '@atlaskit/tooltip';
import { CopyButton } from '@atlassian/bitkit-copy';

import {
  FileTree,
  FileTreeProps,
  FileClickProps,
  TreeEntry,
} from 'src/components/file-tree';
import GenericMessage from 'src/components/generic-message';
import { ExpanderOnChangeEvent } from 'src/components/sidebar';
import { useIntl } from 'src/hooks/intl';
import commonMessages from 'src/i18n/common';
import {
  FileViewMode,
  getGlobalFileViewMode,
  getGlobalIsColorBlindModeEnabled,
} from 'src/redux/diff-settings';
import {
  getPullRequestSourceHash,
  getPullRequestDestinationHash,
} from 'src/redux/pull-request/selectors';
import { usePrFileViewFileAnalytics } from 'src/sections/repository/sections/pull-request/hooks/analytics';
import { scmCommandCode } from 'src/sections/repository/utils/formatted-scm';
import { shortHash } from 'src/utils/short-hash';

import { FileList } from '../file-list/src/file-list';
import { isFile } from '../file-tree/src/flatten-directories';

import messages from './file-tree-card.i18n';
import * as styles from './file-tree-card.style';

export const FILE_TREE_CARD_COLLAPSED_LOCALSTORAGE_KEY =
  'file.tree.card.collapsed';
export type FileTreeCardStateProps = {
  hasConflicts?: boolean;
  hasError?: boolean;
  isTruncated?: boolean;
  untruncatedFileCount: number;
  fileTree: TreeEntry[] | null;
};

export type FileTreeCardExternalProps = Omit<
  FileTreeProps,
  'filteredDiffStat'
> & {
  isCollapsed?: boolean;
  onFileClick?: (event: React.MouseEvent, props: FileClickProps) => void;
  onChange: (event: ExpanderOnChangeEvent) => boolean | undefined;
  onErrorClick?: () => void;
};

export type FileTreeCardProps = FileTreeCardStateProps &
  FileTreeCardExternalProps;

/* eslint @typescript-eslint/ban-types: "warn" */
export const FileTreeCard: React.FC<FileTreeCardProps> = memo(
  (props: FileTreeCardProps) => {
    const {
      isCollapsed,
      hasConflicts,
      fileTree,
      onFileClick,
      hasError,
      isTruncated,
      onErrorClick,
      untruncatedFileCount,
    } = props;
    const intl = useIntl();

    const icon = <DocumentIcon label={intl.formatMessage(messages.label)} />;
    const shouldShowFileList =
      useSelector(getGlobalFileViewMode) === FileViewMode.List;

    const mergeConflictsMessage = intl.formatMessage(messages.mergeConflicts);

    const targetBranchHash = useSelector(getPullRequestDestinationHash) || '';
    const sourceBranchHash = useSelector(getPullRequestSourceHash) || '';
    const targetBranchShortHash = shortHash(targetBranchHash) || '';
    const featureBranchShortHash = shortHash(sourceBranchHash);
    const diffCommand = scmCommandCode(
      targetBranchShortHash,
      featureBranchShortHash
    );
    const isColorBlindModeEnabled = useSelector(
      getGlobalIsColorBlindModeEnabled
    );

    const publishPrAnalyticsFile = usePrFileViewFileAnalytics();
    const onFileClickAnalytics = useCallback(
      (event: React.MouseEvent, clickProps: FileClickProps) => {
        onFileClick!(event, clickProps);
        publishPrAnalyticsFile(
          clickProps.name,
          clickProps.isConflicted,
          clickProps.fileDiffStatus
        );
      },
      [onFileClick, publishPrAnalyticsFile]
    );

    type LinesUpdated = {
      linesAdded: number;
      linesRemoved: number;
    };

    const countLinesUpdated = (contents: TreeEntry[]): LinesUpdated =>
      contents.reduce(
        (totalCount, entry) => {
          const { linesAdded, linesRemoved } = isFile(entry)
            ? { linesAdded: entry.linesAdded, linesRemoved: entry.linesRemoved }
            : countLinesUpdated(entry.contents);

          return {
            linesAdded: totalCount.linesAdded + linesAdded,
            linesRemoved: totalCount.linesRemoved + linesRemoved,
          };
        },
        { linesAdded: 0, linesRemoved: 0 }
      );

    const { linesAdded, linesRemoved } = fileTree
      ? countLinesUpdated(fileTree)
      : { linesAdded: 0, linesRemoved: 0 };

    if (isCollapsed) {
      const content = intl.formatMessage(messages.fileCount, {
        total: untruncatedFileCount,
        formattedCount: untruncatedFileCount,
      });

      return (
        <Tooltip position="left" content={content}>
          <Button
            appearance="subtle"
            iconBefore={
              <styles.IconContainer>
                {icon}{' '}
                {hasConflicts && (
                  <styles.ConflictIndicator
                    aria-label={mergeConflictsMessage}
                  />
                )}
              </styles.IconContainer>
            }
          />
        </Tooltip>
      );
    }

    return (
      <>
        {!!fileTree && !!fileTree.length && !hasError && (
          <>
            {(linesAdded > 0 || linesRemoved > 0) && (
              <styles.LinesUpdatedPanel>
                <styles.LinesUpdatedBody>
                  <styles.LinesUpdatedLabel>
                    <Heading level="h100">
                      {intl.formatMessage(messages.linesUpdatedLabel)}
                    </Heading>
                  </styles.LinesUpdatedLabel>
                  <styles.LinesUpdatedIcons>
                    {linesAdded > 0 && (
                      <styles.LinesAdded
                        cutCorners={linesRemoved > 0}
                        aria-hidden="true"
                        isColorBlindMode={isColorBlindModeEnabled}
                      >
                        <Heading level="h200" as="span">
                          +{linesAdded}
                        </Heading>
                      </styles.LinesAdded>
                    )}
                    {linesRemoved > 0 && (
                      <styles.LinesRemoved
                        cutCorners={linesAdded > 0}
                        aria-hidden="true"
                        isColorBlindMode={isColorBlindModeEnabled}
                      >
                        <Heading level="h200" as="span">
                          -{linesRemoved}
                        </Heading>
                      </styles.LinesRemoved>
                    )}
                  </styles.LinesUpdatedIcons>
                </styles.LinesUpdatedBody>
              </styles.LinesUpdatedPanel>
            )}
            {shouldShowFileList ? (
              <FileList
                fileTree={fileTree}
                expandAll
                onClick={onFileClickAnalytics}
              />
            ) : (
              <FileTree
                fileTree={fileTree}
                expandAll
                onClick={onFileClickAnalytics}
              />
            )}
            {isTruncated && (
              <SectionMessage appearance="information">
                <Heading level="h500">
                  {intl.formatMessage(messages.truncatedContentsMessageTitle)}
                </Heading>
                <FormattedMessage
                  {...messages.truncatedContentsMessageMain}
                  tagName="p"
                />
                <p>
                  <CopyButton
                    shouldMatchTextFieldHeight={false}
                    tooltipPosition="bottom"
                    value={diffCommand}
                  >
                    {intl.formatMessage(messages.truncatedContentsButtonText)}
                  </CopyButton>
                </p>
              </SectionMessage>
            )}
          </>
        )}
        {hasError && (
          <GenericMessage
            iconType="warning"
            title={<FormattedMessage {...messages.errorHeading} />}
          >
            <CustomThemeButton appearance="link" onClick={onErrorClick}>
              <FormattedMessage {...commonMessages.tryAgain} />
            </CustomThemeButton>
          </GenericMessage>
        )}
      </>
    );
  }
);
